import { ReactNode } from 'react';
import { useAuth } from '../hooks/useAuth';
import { UserRole, hasPermission, AuthAction } from '../domain';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredRole?: UserRole | UserRole[];
  requiredPermission?: AuthAction;
  fallback?: ReactNode;
  onUnauthorized?: () => void;
}

export function ProtectedRoute({
  children,
  requiredRole,
  requiredPermission,
  fallback = <UnauthorizedFallback />,
  onUnauthorized,
}: ProtectedRouteProps) {
  const { isAuthenticated, roles } = useAuth();

  if (!isAuthenticated) {
    onUnauthorized?.();
    return fallback;
  }

  if (requiredRole) {
    const requiredRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
    if (!roles.some((r) => requiredRoles.includes(r))) {
      onUnauthorized?.();
      return fallback;
    }
  }

  if (requiredPermission) {
    if (!hasPermission(roles, requiredPermission)) {
      onUnauthorized?.();
      return fallback;
    }
  }

  return children;
}

function UnauthorizedFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 dark:bg-gray-950 sm:px-6 lg:px-8">
      <div className="w-full max-w-md text-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Access Denied</h1>
        <p className="mt-4 text-gray-600 dark:text-gray-400">
          You do not have permission to access this page.
        </p>
        <a
          href="/dashboard"
          className="mt-8 inline-flex h-9 items-center rounded-lg bg-indigo-600 px-4 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
        >
          Go to Dashboard
        </a>
      </div>
    </div>
  );
}
